import {
  LTLQuickQuoteLineItem,
  LTLQuickQuoteRequest,
} from 'lib/api/getLTLQuickQuotes';
import {
  LTL_UNIT_SYSTEMS,
  PACKAGE_TYPE_TO_CODE,
  QuickQuoteInputs,
  QuoteCountries,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { Maybe } from 'types/UtilityTypes';

import { calculateFreightClass } from '../../utils/ltlMetrics';
import {
  LTLCustomerData,
  LTLCustomerStaffData,
  normalizeNumericValue,
  roundToTwoDecimals,
} from './ltlRequestShared';

export type { LTLCustomerData, LTLCustomerStaffData };

type LTLTaiCubicFeetCalculationConfig = {
  heightThresholdInches: number;
  overrideHeightInches: number;
};

/**
 * Builds the LTL Quick Quote API request payload for Tai.
 */
export function buildLTLTaiRequest(
  formValues: QuickQuoteInputs,
  ltlCustomer: Maybe<LTLCustomerData>,
  ltlCustomerStaff: Maybe<LTLCustomerStaffData>,
  ltlQuoteRequestId: number,
  emailId: number,
  threadId: string,
  cubicFeetCalculationConfig?: LTLTaiCubicFeetCalculationConfig,
  // Also gates Tai delivery-window mapping (Estimated/Close). Non-Tai callers
  // of this shared builder still map a single deliveryDate to mustDeliver.
  isTaiLtlIntegration = false
): LTLQuickQuoteRequest {
  const isMetric = formValues.ltl?.commodity?.units === LTL_UNIT_SYSTEMS.METRIC;
  const unitValue = isMetric ? 1 : 0;

  const toInches = (n: number) => (isMetric ? n / 2.54 : n);
  const toLbs = (n: number) => (isMetric ? n * 2.20462 : n);

  const hasCubicFeetConfig =
    cubicFeetCalculationConfig &&
    cubicFeetCalculationConfig.heightThresholdInches > 0 &&
    cubicFeetCalculationConfig.overrideHeightInches > 0;

  const cubicFeetConfig = hasCubicFeetConfig
    ? cubicFeetCalculationConfig
    : undefined;

  const useOverrideHeight = cubicFeetConfig
    ? (formValues.ltl?.items ?? []).some((item) => {
        const height = item.height ?? 0;

        if (height <= 0) {
          return false;
        }

        const heightInches = toInches(height);
        return heightInches > cubicFeetConfig.heightThresholdInches;
      })
    : false;

  const lineItems: LTLQuickQuoteLineItem[] = (formValues.ltl?.items ?? []).map(
    (item) => {
      const parsedHandlingUnits = Number(item.handlingUnits);
      const handlingUnits =
        Number.isFinite(parsedHandlingUnits) && parsedHandlingUnits > 0
          ? parsedHandlingUnits
          : 1;
      const hasDims =
        (item.length ?? 0) > 0 &&
        (item.width ?? 0) > 0 &&
        (item.height ?? 0) > 0;

      const L = toInches(item.length ?? 0);
      const W = toInches(item.width ?? 0);
      const H = toInches(item.height ?? 0);
      const appliedHeightInches =
        useOverrideHeight && cubicFeetConfig && (item.height ?? 0) > 0
          ? cubicFeetConfig.overrideHeightInches
          : H;

      const cubicFeetRaw = hasDims
        ? ((L * W * appliedHeightInches) / 1728) * handlingUnits
        : 0;
      const cubicFeet = roundToTwoDecimals(cubicFeetRaw);
      const cubicMeters = roundToTwoDecimals(cubicFeet * 0.0283168466);

      const weightLbs = toLbs(item.weight ?? 0);
      const poundsPerCubicFeet =
        cubicFeetRaw > 0 && weightLbs > 0
          ? roundToTwoDecimals(weightLbs / cubicFeetRaw)
          : 0;
      const estimatedFreightClass =
        item.estimatedFreightClass ??
        (poundsPerCubicFeet > 0
          ? String(calculateFreightClass(poundsPerCubicFeet))
          : '');
      const selectedFreightClass =
        item.freightClass ?? formValues.ltl?.commodity?.freightClass ?? '';
      const showEstimatedFreightClassMessage =
        item.showEstimatedFreightClassMessage ??
        (estimatedFreightClass !== '' &&
          selectedFreightClass !== '' &&
          estimatedFreightClass !== selectedFreightClass);
      const usingEstimatedFreightClass =
        item.usingEstimatedFreightClass ?? false;

      const packageType = item.packageType ?? PACKAGE_TYPE_TO_CODE['Pallet'];

      return {
        packageType,
        freightClass: selectedFreightClass,
        estimatedFreightClass,
        showEstimatedFreightClassMessage,
        usingEstimatedFreightClass,
        classManuallySelected: false,
        handlingUnits,
        totalWeight: item.weight ?? 0,
        commodityExternalTmsId: item.commodityExternalTmsId ?? '',
        commodityDescription: item.description,
        nmfcNumber: item.nmfcCode ?? '',
        quantityOfGoods: item.pieces ?? 1,
        length: item.length,
        width: item.width,
        height: item.height,
        cubicFeet,
        cubicMeters,
        poundsPerCubicFeet,
      };
    }
  );

  const stops = formValues.stops.map((stop, index) => {
    const isFirstStop = index === 0;
    const isLastStop = index === formValues.stops.length - 1;

    const rawCountry = stop.country || QuoteCountries.USA;
    const address = {
      name: stop.addressName ?? '',
      addressLine1: isTaiLtlIntegration ? (stop.addressLine1 ?? '') : '',
      addressLine2: isTaiLtlIntegration ? (stop.addressLine2 ?? '') : '',
      city: stop.city ?? '',
      state: stop.state ?? '',
      zip: stop.zip ?? '',
      country: rawCountry,
    };

    const baseStop = {
      order: index,
      stopType: isFirstStop ? StopTypes.Pickup : StopTypes.Dropoff,
      address,
    };

    if (isFirstStop) {
      return {
        ...baseStop,
        ...(formValues.pickupDate
          ? { readyTime: formValues.pickupDate.toISOString() }
          : {}),
        ...(formValues.pickupCloseDate
          ? { readyEndTime: formValues.pickupCloseDate.toISOString() }
          : {}),
      };
    }

    if (isLastStop) {
      if (isTaiLtlIntegration) {
        // Tai window: Estimated -> readyTime; Close -> mustDeliver.
        return {
          ...baseStop,
          ...(formValues.deliveryDate
            ? { readyTime: formValues.deliveryDate.toISOString() }
            : {}),
          ...(formValues.deliveryCloseDate
            ? { mustDeliver: formValues.deliveryCloseDate.toISOString() }
            : {}),
        };
      }

      // Shared LTL path (e.g. McLeod): single delivery date is the deadline.
      return {
        ...baseStop,
        ...(formValues.deliveryDate
          ? { mustDeliver: formValues.deliveryDate.toISOString() }
          : {}),
      };
    }

    return baseStop;
  });

  const declaredValue = normalizeNumericValue(
    formValues.ltl?.value?.declaredValue
  );
  const deductibleValue = normalizeNumericValue(
    formValues.ltl?.value?.deductibleValue
  );

  const customerData = {
    name: ltlCustomer?.customerName ?? '',
    tmsIntegrationId: ltlCustomerStaff?.tmsIntegrationId ?? 0,
    ownerName: ltlCustomerStaff?.ownerName ?? '',
    ownerId: ltlCustomerStaff?.ownerId ?? 0,
    externalTMSID: ltlCustomer?.customerId ?? '',
  };

  return {
    emailId,
    threadId,
    quoteRequestId: ltlQuoteRequestId,
    transportType: formValues.transportType,
    customerExternalTmsId: formValues.customerName,
    stops,
    customer: customerData,
    lineItems,
    accessorials: formValues.ltl?.selectedAccessorials ?? [],
    weightUnit: unitValue,
    dimensionUnit: unitValue,
    linearFeet: formValues.ltl?.commodity?.linearFeet,
    searchByTariff: false,
    stackable: formValues.ltl?.commodity?.stackable ?? false,
    isFrontOffice: true,
    shipmentType: 2,
    actualShipmentType: 2,
    trailerType: 0,
    pcmilerRoutingType: 0,
    declaredValue: declaredValue.toString(),
    deductibleValue,
    shiplifyLookups: formValues.ltl?.shiplifyLookups,
  };
}
